/**
 * Searchable target-field picker for a single mapping row — replaces the plain,
 * non-searchable `Select` previously used for the source-column → system-field dropdown
 * (column-mapping-ui spec, "Searchable target-field picker"). Shares its trigger button
 * with `SupplierPicker` (mapping-phase-parts.tsx) via `ComboboxTrigger`.
 *
 * Filtering is manual (`shouldFilter={false}` on `Command`, exact case-insensitive
 * substring match), NOT cmdk's built-in fuzzy filter — a real ~40-entry field catalog
 * (packages/contracts's FIELD_OPTIONS) is broad enough that cmdk's default fuzzy
 * subsequence scoring returns a non-zero match for most typed queries against SOME label,
 * making the "no results" empty state effectively unreachable in production even though it
 * passes in a small-fixture unit test. Exact substring matching keeps the "query matches
 * nothing" contract reliable regardless of catalog size. The "skip this column" option is
 * excluded from this filter (always rendered) rather than forced via cmdk's `forceMount`,
 * since `shouldFilter={false}` already disables cmdk's own filtering entirely.
 */
import * as React from 'react';
import { Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@ooh/ui/components/command';
import { cn } from '@ooh/ui/lib/utils';
import { ComboboxTrigger } from './combobox-trigger';
import type { FieldGroupKey } from './import-field-group-order';
import type { FieldOptionItem } from './mapping-phase';

export interface MappingFieldPickerLabels {
  skipLabel: string;
  searchPlaceholder: string;
  noResultsLabel: string;
}

export interface MappingFieldPickerGroup {
  groupKey: FieldGroupKey;
  label: string;
  options: FieldOptionItem[];
}

export interface MappingFieldPickerProps {
  /** Selected field key, `null`/`undefined` for "skip this column". */
  value: string | null | undefined;
  /** Label to show for the current value when it isn't found in `groupedOptions` (unknown column). */
  unknownValueLabel?: string;
  groupedOptions: MappingFieldPickerGroup[];
  onChange: (value: string | null) => void;
  labels: MappingFieldPickerLabels;
  testId?: string;
}

function matchesQuery(option: FieldOptionItem, query: string): boolean {
  return option.label.toLowerCase().includes(query.toLowerCase());
}

function filterGroups(groups: MappingFieldPickerGroup[], query: string): MappingFieldPickerGroup[] {
  if (!query) return groups;
  return groups
    .map((g) => ({ ...g, options: g.options.filter((o) => matchesQuery(o, query)) }))
    .filter((g) => g.options.length > 0);
}

export function MappingFieldPicker({
  value,
  unknownValueLabel,
  groupedOptions,
  onChange,
  labels,
  testId,
}: MappingFieldPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const selectedOption = React.useMemo(
    () => groupedOptions.flatMap((g) => g.options).find((o) => o.key === value) ?? null,
    [groupedOptions, value],
  );
  const triggerLabel =
    value == null ? labels.skipLabel : (selectedOption?.label ?? unknownValueLabel);
  const visibleGroups = React.useMemo(
    () => filterGroups(groupedOptions, query),
    [groupedOptions, query],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <ComboboxTrigger open={open} label={triggerLabel ?? labels.skipLabel} testId={testId} />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={labels.searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandGroup>
              <CommandItem
                value="__skip__"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn('mr-2 h-4 w-4', value == null ? 'opacity-100' : 'opacity-0')}
                />
                {labels.skipLabel}
              </CommandItem>
            </CommandGroup>
            {visibleGroups.length === 0 ? (
              // A plain element, not cmdk's <CommandEmpty> — with shouldFilter={false} cmdk's own
              // empty-state gate checks its internal registered-item count (never 0 while the
              // always-rendered "skip" CommandItem exists), so <CommandEmpty> silently never
              // renders regardless of this ternary. This message's visibility must come purely
              // from our own `visibleGroups` state, not cmdk's internal bookkeeping.
              <div className="py-4 text-center text-sm text-muted-foreground">
                {labels.noResultsLabel}
              </div>
            ) : null}
            {visibleGroups.map((g) => (
              <CommandGroup key={g.groupKey} heading={g.label}>
                {g.options.map((opt) => (
                  <CommandItem
                    key={opt.key}
                    value={opt.key}
                    onSelect={() => {
                      onChange(opt.key);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === opt.key ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {opt.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
